<template>
  <div class="top-up-modal">
    <!-- 1. Choosing an amount. The tiers are the platform's, read from the server. -->
    <template v-if="step === 'amount'">
      <p class="top-up-modal__lede">{{ t('billing.topUp.lede') }}</p>
      <v-alert v-if="keepsCard" type="info" variant="tonal" density="compact" class="mb-4">
        {{ t('billing.topUp.keepsCard') }}
      </v-alert>
      <!-- A debit already on its way: this top-up is a payment of its own, said before it is one. -->
      <v-alert v-if="incomingCents > 0" type="info" variant="tonal" density="compact" class="mb-4">
        {{ t('billing.topUp.incoming', { amount: price(incomingCents, currency) }) }}
      </v-alert>

      <div v-if="tiersQuery.isLoading.value" class="top-up-modal__centered">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-alert v-else-if="tiersQuery.isError.value" type="error" variant="tonal" density="comfortable">
        {{ readError(tiersQuery.error.value) }}
      </v-alert>

      <template v-else>
        <div v-if="!amountIsLocked" class="top-up-modal__tiers">
          <button
            v-for="tier in tiers"
            :key="tier.min_amount_excl_tax_cents"
            type="button"
            class="top-up-modal__tier"
            :class="{ 'top-up-modal__tier--picked': amountCents === tier.min_amount_excl_tax_cents }"
            @click="amountCents = tier.min_amount_excl_tax_cents"
          >
            <span class="top-up-modal__tier-amount">
              {{ priceExclTax(tier.min_amount_excl_tax_cents, currency) }}
            </span>
            <span v-if="tier.bonus_credits_cents > 0" class="top-up-modal__tier-bonus">
              {{ t('billing.topUp.bonus', { amount: priceExclTax(tier.bonus_credits_cents, currency) }) }}
            </span>
            <span v-else class="top-up-modal__tier-bonus top-up-modal__tier-bonus--none">
              {{ t('billing.topUp.noBonus') }}
            </span>
          </button>
        </div>

        <!-- `lockAmount`: the caller already worked out what is missing, so the amount is a fact
             on this screen rather than a question asked twice. -->
        <v-text-field
          v-if="!amountIsLocked"
          v-model="customAmount"
          :label="t('billing.topUp.customLabel')"
          :hint="t('billing.topUp.customHint', { min: priceExclTax(MIN_CENTS, currency), max: priceExclTax(MAX_CENTS, currency) })"
          persistent-hint
          type="number"
          variant="outlined"
          density="comfortable"
          class="top-up-modal__custom"
        />

        <div class="top-up-modal__recap">
          <div class="top-up-modal__recap-row">
            <span>{{ t('billing.topUp.recap.amount') }}</span>
            <span>{{ priceExclTax(chosenCents, currency) }}</span>
          </div>
          <div v-if="bonusCents > 0" class="top-up-modal__recap-row">
            <span>{{ t('billing.topUp.recap.bonus') }}</span>
            <span class="text-success">+ {{ priceExclTax(bonusCents, currency) }}</span>
          </div>
          <div class="top-up-modal__recap-row top-up-modal__recap-row--total">
            <span>{{ t('billing.topUp.recap.credited') }}</span>
            <span>{{ priceExclTax(chosenCents + bonusCents, currency) }}</span>
          </div>
          <p class="top-up-modal__recap-note">{{ t('billing.topUp.recap.taxNote') }}</p>
        </div>

        <div class="top-up-modal__actions">
          <v-btn variant="text" @click="close()">{{ t('core.common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!isAmountValid"
            :loading="isOpening"
            @click="proceedFromAmount"
          >
            {{ t('billing.topUp.confirm') }}
          </v-btn>
        </div>
      </template>
    </template>

    <!-- 1b. The billing identity — inserted only while the profile is incomplete, so it is asked
         once and never again. Nothing here mentions VAT: whether it applies is only known once
         this step has been answered. Saving it goes straight on to the payment. -->
    <BillingIdentityForm
      v-else-if="step === 'billing'"
      :intro="t('billing.identity.intro')"
      :submit-label="t('billing.topUp.confirm')"
      :show-back="!amountIsLocked"
      :show-saved-notice="false"
      @back="step = 'amount'"
      @saved="startPurchase"
    />

    <!-- 2. Opening the sale and the attempt. Both are one gesture for the person. -->
    <div v-else-if="step === 'opening'" class="top-up-modal__centered">
      <v-progress-circular indeterminate color="primary" size="44" />
      <p class="top-up-modal__waiting">{{ t('billing.topUp.opening') }}</p>
    </div>

    <!-- 3. The provider's payment form, on the attempt's own key and account. It stays mounted
         while a confirmation runs so the provider's own dialog has somewhere to come back to. -->
    <template v-if="step === 'payment' || step === 'confirming'">
      <PurchaseRecap v-if="purchase" :purchase="purchase" class="mb-4" />
      <v-alert v-if="keepsCard" type="info" variant="tonal" density="compact" class="mb-4">
        {{ t('billing.topUp.keepsCard') }}
      </v-alert>
      <!-- A debit already on its way: this top-up is a payment of its own, said before it is one. -->
      <v-alert v-if="incomingCents > 0" type="info" variant="tonal" density="compact" class="mb-4">
        {{ t('billing.topUp.incoming', { amount: price(incomingCents, currency) }) }}
      </v-alert>

      <div v-if="!payment.ready.value" class="top-up-modal__centered top-up-modal__centered--tight">
        <v-progress-circular indeterminate color="primary" />
        <p class="top-up-modal__waiting">{{ t('billing.topUp.payment.loading') }}</p>
      </div>
      <div ref="elementHost" class="top-up-modal__element" />

      <!-- A direct debit credits nothing for days: said while it is the method picked, so the
           payer chooses knowing it rather than learning it from a wallet that did not move. -->
      <v-alert
        v-if="payment.method.value === 'sepa_debit'"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mt-4"
      >
        {{ t('billing.topUp.payment.sepaNotice') }}
      </v-alert>

      <v-alert
        v-if="formProblem"
        type="warning"
        variant="tonal"
        density="comfortable"
        class="mt-4"
      >
        {{ formProblem }}
      </v-alert>

      <!-- The terms of sale make any purchase an acceptance of them, so the payer sees them at the
           moment of paying rather than being told so in the document itself. -->
      <i18n-t keypath="billing.topUp.legalAccept" tag="p" class="top-up-modal__legal" scope="global">
        <template #terms>
          <a :href="videasPageUrl('termsOfSale', locale)" target="_blank" rel="noopener">{{
            t('billing.topUp.termsOfSale')
          }}</a>
        </template>
        <template #use>
          <a :href="videasPageUrl('termsOfUse', locale)" target="_blank" rel="noopener">{{
            t('billing.topUp.termsOfUse')
          }}</a>
        </template>
      </i18n-t>

      <div class="top-up-modal__actions">
        <v-btn variant="text" :disabled="step === 'confirming'" @click="close()">
          {{ t('core.common.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!payment.ready.value"
          :loading="step === 'confirming'"
          @click="pay"
        >
          {{ payLabel }}
        </v-btn>
      </div>
    </template>

    <!-- 4. The provider has it; the credits follow the invoicing service's event, and nothing
         here says they arrived before the top-up reads `completed`. -->
    <div v-else-if="step === 'waiting'" class="top-up-modal__centered">
      <v-progress-circular indeterminate color="primary" size="44" />
      <p class="top-up-modal__waiting">{{ t('billing.topUp.payment.waiting') }}</p>
    </div>

    <!-- 5a. The top-up reads `completed`: the credits are in the wallet. -->
    <template v-else-if="step === 'paid'">
      <v-alert type="success" variant="tonal" density="comfortable" class="mb-4">
        {{ t('billing.topUp.paid') }}
      </v-alert>
      <PurchaseRecap v-if="purchase" :purchase="purchase" />
      <div class="top-up-modal__actions">
        <v-btn color="primary" variant="flat" @click="confirm(purchase)">{{ t('billing.common.done') }}</v-btn>
      </div>
    </template>

    <!-- 5b. The provider has the payment and the credits have not landed yet. `collecting`: the
         invoicing service says the bank is collecting a debit of this top-up, whose credits land
         when the bank answers, in days. `processing`: nothing says yet which it is — a debit not
         recorded yet, a card whose event is late — so only what is true of both is said. -->
    <template v-else-if="step === 'collecting' || step === 'processing'">
      <v-alert type="info" variant="tonal" density="comfortable" class="mb-4">
        {{
          step === 'collecting'
            ? t('billing.topUp.payment.collecting')
            : t('billing.topUp.payment.processing')
        }}
      </v-alert>
      <PurchaseRecap v-if="purchase" :purchase="purchase" />
      <div class="top-up-modal__actions">
        <v-btn variant="text" @click="goToHistory">{{ t('billing.topUp.seeHistory') }}</v-btn>
        <v-btn color="primary" variant="flat" @click="confirm(purchase)">{{ t('billing.common.done') }}</v-btn>
      </div>
    </template>

    <!-- 5c. The bank still wants the payer, and the dialog was left. -->
    <template v-else-if="step === 'action'">
      <v-alert type="warning" variant="tonal" density="comfortable" class="mb-4">
        {{ t('billing.topUp.payment.requiresAction') }}
      </v-alert>
      <div class="top-up-modal__actions">
        <v-btn variant="text" @click="close()">{{ t('core.common.close') }}</v-btn>
        <v-btn color="primary" variant="flat" :loading="isOpening" @click="retryPayment">
          {{ t('billing.topUp.payment.tryAgain') }}
        </v-btn>
      </div>
    </template>

    <!-- 5d. This attempt is over. Another is a new attempt, on a new key. -->
    <template v-else-if="step === 'declined'">
      <v-alert type="error" variant="tonal" density="comfortable" class="mb-4">
        <div class="font-weight-medium mb-1">{{ t('billing.topUp.payment.declined') }}</div>
        <p v-if="declineMessage" class="top-up-modal__failure">{{ declineMessage }}</p>
      </v-alert>
      <PurchaseRecap v-if="purchase" :purchase="purchase" />
      <div class="top-up-modal__actions">
        <v-btn variant="text" @click="close()">{{ t('core.common.close') }}</v-btn>
        <v-btn color="primary" variant="flat" :loading="isOpening" @click="retryPayment">
          {{ t('billing.topUp.payment.tryAgain') }}
        </v-btn>
      </div>
    </template>

    <!-- 5e. The attempt came back with nothing a browser can confirm it with: an invoicing
         service that predates the publishable key, or an attempt with no secret left. -->
    <template v-else-if="step === 'uncollectable'">
      <v-alert type="warning" variant="tonal" density="comfortable" class="mb-4">
        <div class="font-weight-medium mb-1">{{ t('billing.topUp.uncollectable.title') }}</div>
        {{ t('billing.topUp.uncollectable.body') }}
      </v-alert>
      <PurchaseRecap v-if="purchase" :purchase="purchase" />
      <div class="top-up-modal__actions">
        <v-btn variant="text" @click="close()">{{ t('core.common.close') }}</v-btn>
        <v-btn color="primary" variant="flat" @click="goToHistory">
          {{ t('billing.topUp.seeHistory') }}
        </v-btn>
      </div>
    </template>

    <!-- 6. It failed on our side or at the invoicing service. The server's own words, and the id
         to quote. -->
    <template v-else-if="step === 'failed'">
      <v-alert type="error" variant="tonal" density="comfortable" class="mb-4">
        <div class="font-weight-medium mb-1">{{ t('billing.topUp.failed.title') }}</div>
        <p class="top-up-modal__failure">{{ failure.message }}</p>
        <p v-if="failure.requestId" class="top-up-modal__request-id">
          {{ t('billing.topUp.failed.requestId', { id: failure.requestId }) }}
        </p>
      </v-alert>

      <v-alert
        v-if="purchase"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mb-4"
      >
        {{ t('billing.topUp.failed.purchaseSurvives') }}
      </v-alert>

      <div class="top-up-modal__actions">
        <v-btn variant="text" @click="close()">{{ t('core.common.close') }}</v-btn>
        <v-btn v-if="purchase" color="primary" variant="flat" @click="goToHistory">
          {{ t('billing.topUp.seeHistory') }}
        </v-btn>
        <v-btn v-else color="primary" variant="flat" @click="restart">
          {{ t('billing.topUp.failed.startAgain') }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * Buying credits: the amount, the sale, the attempt, the provider's form — and waiting for the
 * credits rather than announcing them.
 *
 * **The pay gesture is two calls and one key.** `POST …/purchases` opens the sale at the invoicing
 * service under an `Idempotency-Key` this modal mints **once**, so a person who clicks twice, or a
 * retry after a timeout, lands on one row and one sale; `POST …/{id}/payments` then opens the
 * attempt under a **new** key every time, because a declined attempt is terminal over there and
 * the new one cancels it at the provider.
 *
 * **The browser confirms with the provider's own script**, on the three values the attempt
 * answers — the client secret, the publishable key and the account (`useStripePayment`,
 * `docs/plans/billing-ledger.md` B7f). A card's 3-D Secure is the provider's dialog over this
 * modal; a method that has to leave the page comes back to the top-up history with the purchase in
 * the address, and the history reopens this modal on it.
 *
 * **Nothing here says credits arrived before the top-up reads `completed`, nor that a debit is
 * being collected before the top-up says so.** What the provider answers is whether it collected;
 * whether the wallet was credited is the invoicing service's `sale.paid`, and whether the bank is
 * collecting a debit its `payment.processing`, both drained by the worker. So a confirmation is
 * followed by re-reading the purchase for a bounded while until it says one of the two
 * (`paymentWait.ts`), and past that the modal says the payment is being confirmed — the history's
 * reconciliation is where a late event is chased.
 *
 * **Nothing retries by itself.** A failure at the invoicing service is not retryable on our side
 * (`docs/plans/billing-ledger.md`, the audit of 2026-09-15), so a refusal is shown with the
 * server's own message and the request id to quote, and the person decides.
 */
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { refusalMessage } from '@/apps/core/utils'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useTheme } from 'vuetify'
import { useQueryClient } from '@tanstack/vue-query'
import { ApiError } from '@/api/client'
import {
  readCreditPurchase,
  type CreditPaymentOpened,
  type CreditPurchaseView,
} from '@/api/billing/purchases'
import { readAutoTopUp } from '@/api/billing/autoTopUp'
import { MODAL_CLOSE_KEY, MODAL_CONFIRM_KEY } from '@/apps/core/types/modal.types'
import { isBillingProfileComplete } from '@/apps/billing/utils/billingProfile'
import { videasPageUrl } from '@/videasLinks'
import { useMoney } from '../composables/useMoney'
import {
  useBillingProfile,
  useCreditBonusTiers,
  useOpenCreditPayment,
  useOpenCreditPurchase,
  useWallet,
  walletKeys,
} from '../queries'
import { useStripePayment, type ConfirmOutcome } from '../composables/useStripePayment'
import { waitForOutcome, type WaitResult } from '../paymentWait'
import PurchaseRecap from '../components/PurchaseRecap.vue'
import BillingIdentityForm from '../components/BillingIdentityForm.vue'

const props = withDefaults(
  defineProps<{
    /** An amount in cents the caller already worked out, pre-filled. */
    suggestedAmount?: number | null
    /** With a suggested amount: show it as settled rather than offering the choice again. */
    lockAmount?: boolean
    /**
     * A top-up already opened, to pay rather than to open: the history's *pay* button, and the
     * link a mail sends somebody to. The modal starts at the payment.
     */
    purchaseId?: string | null
    /**
     * The provider's `redirect_status` when the payer came back from a page the payment had to
     * leave for. With [`purchaseId`], the modal starts by waiting on that top-up.
     */
    returnedStatus?: string | null
    /**
     * Keep the card this top-up is paid with, for the automatic top-up — which must be switched on
     * already: the consent it records is what the card is kept under, and the server refuses
     * otherwise (`docs/plans/billing-ledger.md` B8).
     */
    saveInstrument?: boolean
  }>(),
  {
    suggestedAmount: null,
    lockAmount: false,
    purchaseId: null,
    returnedStatus: null,
    saveInstrument: false,
  },
)

/** Locked only when there is in fact an amount to lock: a `true` with no amount is a bug upstream. */
const amountIsLocked = computed(() => props.lockAmount && (props.suggestedAmount ?? 0) > 0)

/** The band the surface accepts, in cents excluding tax. Outside it the route answers 400. */
const MIN_CENTS = 1_000
const MAX_CENTS = 500_000

/**
 * How long a confirmed payment is waited on before the modal says it is being confirmed. Two
 * minutes: a card's `sale.paid` and a debit's `payment.processing` are seconds away, and a debit's
 * credits are days — waiting longer would keep a person in front of a spinner for an answer that
 * is not coming today.
 */
const WAIT_FOR_CREDITS_MS = 120_000

const { t, locale } = useI18n()
const { price, priceExclTax } = useMoney()
const route = useRoute()
const router = useRouter()
const theme = useTheme()
const queryClient = useQueryClient()
const close = inject<(data?: unknown) => void>(MODAL_CLOSE_KEY, () => {})
const confirm = inject<(data?: unknown) => void>(MODAL_CONFIRM_KEY, () => {})

const tiersQuery = useCreditBonusTiers()
const wallet = useWallet()
/** The credits of a top-up whose debit is being collected, which a new top-up would add to. */
const incomingCents = computed(() => wallet.data.value?.incoming_cents ?? 0)
const tiers = computed(() => tiersQuery.data.value ?? [])
// The wallet's own currency, which is `EUR` until the invoicing service carries a second one
// (`CreditBalanceView.currency` says so). Hard-coded rather than read, because this modal opens
// from screens that have not read the wallet — a spinner for a formatting hint would be worse.
const currency = 'EUR'

type Step =
  | 'amount'
  | 'billing'
  | 'opening'
  | 'payment'
  | 'confirming'
  | 'waiting'
  | 'paid'
  | 'collecting'
  | 'processing'
  | 'action'
  | 'declined'
  | 'uncollectable'
  | 'failed'
const step = ref<Step>(props.purchaseId ? 'opening' : 'amount')
const purchase = ref<CreditPurchaseView | null>(null)
const failure = ref<{ message: string; requestId?: string }>({ message: '' })
const declineMessage = ref('')
const formProblem = ref('')

const amountCents = ref<number>(props.suggestedAmount ?? 0)
const customAmount = ref<string>(props.suggestedAmount ? String(props.suggestedAmount / 100) : '')

/**
 * The key of **this pay gesture**, minted once. A second click on a failed open sends it again and
 * the backend answers the row it already wrote, without dialling the invoicing service twice.
 */
const idempotencyKey = ref(crypto.randomUUID())

const openPurchase = useOpenCreditPurchase()
const openPayment = useOpenCreditPayment()
const isCheckingProfile = ref(false)
const isOpening = computed(
  () => isCheckingProfile.value || openPurchase.isPending.value || openPayment.isPending.value,
)

/**
 * The billing identity gates the payment, not the amount: an invoice must name and locate who is
 * buying. Read here so it is usually in the cache before the amount is confirmed.
 */
const profileQuery = useBillingProfile()

const payment = useStripePayment()
const elementHost = ref<HTMLElement | null>(null)

/**
 * Whether the card this payment is confirmed with is kept for the automatic top-up.
 *
 * Asked by the caller, or — for an automatic top-up's own sale, paid on session because the bank
 * asked for the customer — by that sale while its arrangement is on: paying it **confirms the card
 * again** (`docs/plans/billing-ledger.md` D3), so the card confirmed is the one kept for the next.
 */
const keepsAutoTopUpCard = ref(false)
const keepsCard = computed(() => props.saveInstrument || keepsAutoTopUpCard.value)

/** Set when the modal goes away, so a wait in flight stops re-reading for nobody. */
let disposed = false
onBeforeUnmount(() => {
  disposed = true
})

// A typed amount wins over a tier button, so the two controls never disagree about what is bought.
const typedCents = computed(() => {
  const euros = Number.parseFloat(customAmount.value.replace(',', '.'))
  return Number.isFinite(euros) && euros > 0 ? Math.round(euros * 100) : null
})
const chosenCents = computed(() => typedCents.value ?? amountCents.value)
const isAmountValid = computed(() => chosenCents.value >= MIN_CENTS && chosenCents.value <= MAX_CENTS)

/**
 * The bonus the chosen amount earns, taken from the **highest tier it reaches** and from that
 * tier's own published figure rather than from a percentage recomputed here — the server rounds,
 * and a recap that rounded differently would promise a euro the wallet does not receive.
 */
const bonusCents = computed(() => {
  const reached = tiers.value
    .filter((tier) => chosenCents.value >= tier.min_amount_excl_tax_cents)
    .at(-1)
  if (!reached || reached.bonus_basis_points <= 0) return 0
  return Math.floor((chosenCents.value * reached.bonus_basis_points) / 10_000)
})

/** What the button asks for: the total including tax the invoicing service computed. */
const payLabel = computed(() => {
  const total = purchase.value?.total_incl_tax_cents
  return typeof total === 'number'
    ? t('billing.topUp.payment.pay', { amount: price(total, currency) })
    : t('billing.topUp.payment.payUnknown')
})

/**
 * A failure as this flow says it. A refused call or a network down is the common sentence; any other
 * `Error` but a script's own bug (`TypeError`) is one the flow raised itself — the payment script
 * that did not load, the payment provider's own refusal — and its message is already written for
 * the person paying.
 */
function readError(error: unknown): string {
  const common = refusalMessage(error)
  if (common) return common
  if (error instanceof Error && !(error instanceof ApiError || error instanceof TypeError) && error.message) {
    return error.message
  }
  return t('core.common.errorLoading')
}

function recordFailure(error: unknown): void {
  failure.value =
    error instanceof ApiError
      ? { message: refusalMessage(error) ?? t('core.errors.serverError'), requestId: error.requestId }
      : { message: readError(error) }
  step.value = 'failed'
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** The theme's primary colour when it is a plain `#rrggbb`, which is what the provider takes. */
function primaryColor(): string | undefined {
  const color = theme.global.current.value.colors.primary
  return typeof color === 'string' && /^#[0-9a-f]{6}$/i.test(color) ? color : undefined
}

/** Where a payment that had to leave the page comes back to: the history, on this top-up. */
function returnUrl(uid: string): string {
  const organizationId = route.params.organizationId
  const target = router.resolve({
    name: 'settings:billing-history',
    params: { organizationId: typeof organizationId === 'string' ? organizationId : '' },
    query: { purchase: uid },
  })
  return new URL(target.href, window.location.origin).toString()
}

/**
 * Whether the billing step is owed. A failed read does not hold the purchase back: the top-up goes
 * on as it would without the step rather than stopping somebody who has already answered — the
 * backend creates the customer from the organization's name when there is none.
 */
async function needsBillingStep(): Promise<boolean> {
  if (profileQuery.data.value !== undefined) return !isBillingProfileComplete(profileQuery.data.value)
  const answer = await profileQuery.refetch()
  return answer.data === undefined ? false : !isBillingProfileComplete(answer.data)
}

/** Leaving the amount: the billing identity first while it is incomplete, then the payment. */
async function proceedFromAmount(): Promise<void> {
  if (!isAmountValid.value) return
  isCheckingProfile.value = true
  let needed: boolean
  try {
    needed = await needsBillingStep()
  } finally {
    isCheckingProfile.value = false
  }
  if (needed) {
    step.value = 'billing'
    return
  }
  await startPurchase()
}

async function startPurchase(): Promise<void> {
  if (!isAmountValid.value) return
  step.value = 'opening'

  try {
    purchase.value = await openPurchase.mutateAsync({
      amountExclTaxCents: chosenCents.value,
      idempotencyKey: idempotencyKey.value,
    })
  } catch (error) {
    recordFailure(error)
    return
  }

  await openAttempt()
}

/**
 * Open an attempt on the top-up in hand and put the provider's form in front of the payer — or say
 * why there is nothing to confirm.
 */
async function openAttempt(): Promise<void> {
  if (!purchase.value) return
  step.value = 'opening'
  formProblem.value = ''

  let attempt: CreditPaymentOpened
  try {
    attempt = await openPayment.mutateAsync({
      purchaseId: purchase.value.uid,
      saveInstrument: keepsCard.value,
    })
  } catch (error) {
    // The sale is open whatever happened to the attempt, so the failure screen says so and the
    // history is where it is resolved.
    recordFailure(error)
    return
  }

  if (!attempt.client_secret) {
    // Nothing left to confirm: an attempt the provider already settled, one being collected, or
    // one that ended. What the credits did is the top-up's to say.
    if (attempt.status === 'processing') await waitForCollection()
    else await waitForCredits(attempt.status === 'succeeded' || attempt.status === 'pending')
    return
  }
  if (!attempt.publishable_key) {
    step.value = 'uncollectable'
    return
  }

  step.value = 'payment'
  await nextTick()
  if (!elementHost.value) return
  try {
    await payment.mount(
      {
        clientSecret: attempt.client_secret,
        publishableKey: attempt.publishable_key,
        stripeAccount: attempt.stripe_account ?? null,
      },
      elementHost.value,
      {
        locale: locale.value,
        dark: theme.global.current.value.dark,
        primary: primaryColor(),
      },
    )
  } catch {
    recordFailure(new Error(t('billing.topUp.payment.scriptUnavailable')))
  }
}

async function pay(): Promise<void> {
  if (!purchase.value) return
  step.value = 'confirming'
  formProblem.value = ''

  let outcome: ConfirmOutcome
  try {
    outcome = await payment.confirm(returnUrl(purchase.value.uid))
  } catch (error) {
    recordFailure(error)
    return
  }

  switch (outcome.kind) {
    case 'invalid':
      formProblem.value = outcome.message
      step.value = 'payment'
      return
    case 'declined':
      payment.unmount()
      declineMessage.value = outcome.message
      step.value = 'declined'
      return
    case 'requires_action':
      payment.unmount()
      step.value = 'action'
      return
    case 'succeeded':
      payment.unmount()
      await waitForCredits(true)
      return
    case 'processing':
      payment.unmount()
      await waitForCollection()
      return
  }
}

/**
 * Re-read the top-up for [`WAIT_FOR_CREDITS_MS`] at most, until it says what became of the
 * payment, and draw what it says: `paid`, `declined` — a refusal recorded since the payer
 * confirmed —, `collecting` — when `acceptCollecting` lets a debit in flight end the wait — or,
 * past the bound, the neutral `processing`.
 */
async function awaitOutcome(acceptCollecting: boolean): Promise<void> {
  if (!purchase.value) return
  const uid = purchase.value.uid
  const failedBefore = purchase.value.payment_failed_at ?? null
  const deadline = Date.now() + WAIT_FOR_CREDITS_MS
  step.value = 'waiting'

  let result: WaitResult<CreditPurchaseView>
  try {
    result = await waitForOutcome(() => readCreditPurchase(uid), {
      acceptCollecting,
      failedBefore,
      timeoutMs: WAIT_FOR_CREDITS_MS,
      sleep,
      stopped: () => disposed,
    })
  } catch (error) {
    recordFailure(error)
    return
  }
  if (!showOutcome(result)) return
  if (result.outcome === 'collecting') await watchCollection(uid, failedBefore, deadline)
}

/** Draw how a wait ended; `false` when it was stopped with nothing to draw. */
function showOutcome(result: WaitResult<CreditPurchaseView>): boolean {
  if (result.purchase) purchase.value = result.purchase
  if (!result.outcome) return false
  // The refusal's reason is the recap's to say, from the row; the provider's words in the browser
  // were about a payment it had accepted.
  if (result.outcome === 'declined') declineMessage.value = ''
  void queryClient.invalidateQueries({ queryKey: walletKeys.all })
  step.value = result.outcome
  return true
}

/**
 * The debit is said at once, and the modal goes on reading while it is open, to the same
 * deadline: a bank that answers within it — paid, or refused — replaces *collecting* with what it
 * answered. Past the deadline, or on a read that fails, *collecting* stays as it is: it is still
 * true of a debit nobody has answered.
 */
async function watchCollection(uid: string, failedBefore: string | null, deadline: number): Promise<void> {
  const remaining = deadline - Date.now()
  if (remaining <= 0) return
  let result: WaitResult<CreditPurchaseView>
  try {
    result = await waitForOutcome(() => readCreditPurchase(uid), {
      acceptCollecting: false,
      failedBefore,
      timeoutMs: remaining,
      sleep,
      stopped: () => disposed || step.value !== 'collecting',
    })
  } catch {
    return
  }
  if (result.outcome === 'paid' || result.outcome === 'declined') showOutcome(result)
}

/**
 * The provider answered `processing`: a direct debit on its way — or, for seconds, a card. The
 * top-up says which once the invoicing service's event lands: `completed` for the card, a debit
 * being collected for the other — which the modal then says at once, rather than waiting on
 * credits that land days later.
 */
async function waitForCollection(): Promise<void> {
  await awaitOutcome(true)
}

/**
 * After the provider reported the money taken (`collected`), wait for the top-up to complete — and
 * past the bound say it is being confirmed. When it did not, the top-up is read once and shown as
 * it stands.
 */
async function waitForCredits(collected: boolean): Promise<void> {
  if (collected) {
    await awaitOutcome(false)
    return
  }
  if (!purchase.value) return
  step.value = 'waiting'
  try {
    purchase.value = await readCreditPurchase(purchase.value.uid)
  } catch (error) {
    recordFailure(error)
    return
  }
  void queryClient.invalidateQueries({ queryKey: walletKeys.all })
  step.value = purchase.value.status === 'completed' ? 'paid' : 'declined'
}

/** Another attempt on the same top-up: a new key, a new intent, the old one cancelled over there. */
async function retryPayment(): Promise<void> {
  declineMessage.value = ''
  await openAttempt()
}

/** Start over with a **new** key: nothing was written, so this is a new gesture and not a retry. */
function restart(): void {
  idempotencyKey.value = crypto.randomUUID()
  purchase.value = null
  failure.value = { message: '' }
  step.value = 'amount'
}

function goToHistory(): void {
  const organizationId = route.params.organizationId
  close()
  if (typeof organizationId === 'string' && organizationId !== '') {
    void router.push({ name: 'settings:billing-history', params: { organizationId } })
  }
}

/**
 * A top-up handed in by the caller: read it, then either wait on it — the payer came back from a
 * page the provider sent them to — or pay it.
 */
onMounted(async () => {
  if (!props.purchaseId) return
  try {
    purchase.value = await readCreditPurchase(props.purchaseId)
  } catch (error) {
    recordFailure(error)
    return
  }
  if (purchase.value.status === 'completed') {
    step.value = 'paid'
    return
  }
  if (purchase.value.kind === 'credit_auto_top_up' && !props.returnedStatus) {
    // A read that fails keeps nothing: the payment goes through all the same.
    keepsAutoTopUpCard.value = await readAutoTopUp()
      .then((arrangement) => arrangement.enabled)
      .catch(() => false)
  }
  if (props.returnedStatus) {
    if (props.returnedStatus === 'failed') {
      step.value = 'declined'
      return
    }
    if (props.returnedStatus === 'processing') {
      await waitForCollection()
      return
    }
    await waitForCredits(true)
    return
  }
  await openAttempt()
})
</script>

<style scoped lang="scss">
@use '@/styles/design-tokens' as *;

.top-up-modal {
  padding: $space-lg;

  &__lede {
    margin: 0 0 $space-lg;
    font-size: $text-md;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  &__centered {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-md;
    padding: $space-2xl 0;

    &--tight {
      padding: $space-lg 0;
    }
  }

  &__waiting {
    margin: 0;
    font-size: $text-md;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    text-align: center;
  }

  &__element {
    min-height: 1px;
  }

  &__tiers {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: $space-sm;
    margin-bottom: $space-lg;
  }

  &__tier {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: $space-md;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: $radius-lg;
    background: rgb(var(--v-theme-surface));
    cursor: pointer;
    text-align: left;

    &--picked {
      border-color: rgb(var(--v-theme-primary));
      background: rgba(var(--v-theme-primary), 0.06);
    }
  }

  &__tier-amount {
    font-weight: $font-semibold;
    font-variant-numeric: tabular-nums;
  }

  &__tier-bonus {
    font-size: $text-xs;
    color: rgb(var(--v-theme-success));

    &--none {
      color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
    }
  }

  &__custom {
    margin-bottom: $space-lg;
  }

  &__recap {
    padding: $space-md $space-lg;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: $radius-lg;
  }

  &__recap-row {
    display: flex;
    justify-content: space-between;
    font-size: $text-md;
    padding: 2px 0;

    &--total {
      font-weight: $font-semibold;
      margin-top: $space-sm;
      padding-top: $space-sm;
      border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
  }

  &__recap-note {
    margin: $space-sm 0 0;
    font-size: $text-xs;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
  }

  &__legal {
    margin: $space-lg 0 0;
    font-size: $text-xs;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);

    a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }

  &__failure {
    margin: 0;
  }

  &__request-id {
    margin: $space-xs 0 0;
    font-size: $text-xs;
    font-family: monospace;
    opacity: 0.8;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: $space-sm;
    margin-top: $space-lg;
  }
}
</style>
